"use client";

import * as PopoverPrimitive from "@radix-ui/react-popover";
import cn from "classnames";
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef, useRef } from "react";

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverContent = forwardRef<
	ElementRef<typeof PopoverPrimitive.Content>,
	ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(
	(
		{
			className,
			align = "center",
			sideOffset = 4,
			onCloseAutoFocus,
			onPointerDownOutside,
			...props
		},
		ref
	) => {
		const pointerDownOutsideRef = useRef(false);

		return (
			<PopoverPrimitive.Portal>
				<PopoverPrimitive.Content
					ref={ref}
					align={align}
					sideOffset={sideOffset}
					onPointerDownOutside={(event) => {
						pointerDownOutsideRef.current = true;
						onPointerDownOutside?.(event);
					}}
					onCloseAutoFocus={(event) => {
						onCloseAutoFocus?.(event);

						if (pointerDownOutsideRef.current) {
							pointerDownOutsideRef.current = false;
							if (event.defaultPrevented) return;
							event.preventDefault();
						}
					}}
					className={cn(
						"z-popover",
						//this checks if the className passed into the component doesn't have a tailwind width class
						// if it doesn't it defaults to width of 72
						// this is important so we don't have to use ! to override the default width
						!className?.match(/\bw-\w+/) && "w-72",
						"text-sm",
						"rounded-lg",
						"border",
						"bg-primary-raised",
						"p-4",
						"shadow-xl",
						"outline-none",
						"data-[state=open]:animate-in",
						"data-[state=closed]:animate-out",
						"data-[state=closed]:fade-out-0",
						"data-[state=open]:fade-in-0",
						"data-[state=closed]:zoom-out-95",
						"data-[state=open]:zoom-in-95",
						"data-[side=bottom]:slide-in-from-top-2",
						"data-[side=left]:slide-in-from-right-2",
						"data-[side=right]:slide-in-from-left-2",
						"data-[side=top]:slide-in-from-bottom-2",
						className
					)}
					{...props}
				/>
			</PopoverPrimitive.Portal>
		);
	}
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent };
